import { useState } from 'react';
import { Ticket, Loader2, Plus, X, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';
import type { CreateEventBody } from '@/api/events.api';

/**
 * Form de configuración del minijuego "Raspá y Ganá" (familia pick).
 *
 * Clona la pantalla "Crear Raspa y Gana" de fedesuete (ver fede-raspa.png):
 *  - Nombre de la campaña *
 *  - Duración (minutos) * — se cierra automáticamente después de este tiempo
 *  - Premios (probabilidad total debe sumar 100%):
 *      emoji · label · tipo (Fichas/Bono100/Bono200/Sin premio) · monto $ · peso %  · eliminar
 *  - "+ Agregar premio"  ·  "Total: X%"
 *  - Botones: Guardar Borrador · Crear y Activar · Guardar como Template
 *
 * Genera el `config` JSONB y llama `eventsApi.create({ type:'raspa', ... })`
 * vía el hook `useCreateEvent`.
 */

type PrizeKind = 'fichas' | 'bono100' | 'bono200' | 'none';

interface PrizeRow {
  emoji: string;
  label: string;
  prizeKind: PrizeKind;
  amount: string;
  weight: string;
}

const KIND_OPTIONS: { value: PrizeKind; label: string }[] = [
  { value: 'fichas', label: 'Fichas' },
  { value: 'bono100', label: 'Bono 100%' },
  { value: 'bono200', label: 'Bono 200%' },
  { value: 'none', label: 'Sin premio' },
];

/** Emojis sugeridos para el selector rápido de cada fila. */
const EMOJI_CHOICES = ['💎', '🌟', '🍀', '❌', '🎁', '💰', '🔔', '⭐', '🍒', '👑'];

const DEFAULT_ROWS: PrizeRow[] = [
  { emoji: '💎', label: 'Premio Mayor', prizeKind: 'fichas', amount: '10000', weight: '5' },
  { emoji: '🌟', label: 'Premio Medio', prizeKind: 'fichas', amount: '2000', weight: '15' },
  { emoji: '🍀', label: 'Premio Menor', prizeKind: 'fichas', amount: '500', weight: '30' },
  { emoji: '❌', label: 'Sin premio', prizeKind: 'none', amount: '0', weight: '50' },
];

interface RaspaFormProps {
  /** Callback opcional tras crear (cerrar dialog, refrescar tab, etc.). */
  onCreated?: () => void;
}

export function RaspaForm({ onCreated }: RaspaFormProps) {
  const createMutation = useCreateEvent();

  const [name, setName] = useState('');
  const [durationMin, setDurationMin] = useState('15');
  const [rows, setRows] = useState<PrizeRow[]>(DEFAULT_ROWS.map((r) => ({ ...r })));
  const [error, setError] = useState('');

  const totalWeight = rows.reduce((acc, r) => acc + (Number(r.weight) || 0), 0);
  const totalOk = totalWeight === 100;

  const updateRow = (idx: number, patch: Partial<PrizeRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { emoji: '🎁', label: 'Nuevo premio', prizeKind: 'fichas', amount: '0', weight: '0' },
    ]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  const buildBody = (status: 'draft' | 'active'): CreateEventBody => {
    const dur = Number(durationMin) || 0;
    const prizes = rows.map((r) => ({
      label: r.label.trim() || 'Premio',
      emoji: r.emoji || '🎁',
      prizeKind: r.prizeKind,
      amount: r.prizeKind === 'none' ? 0 : Number(r.amount) || 0,
      weight: Number(r.weight) || 0,
    }));

    // Premio "principal" (mayor monto != none) para los campos planos del Event.
    const top = [...prizes]
      .filter((p) => p.prizeKind !== 'none')
      .sort((a, b) => b.amount - a.amount)[0];

    return {
      // 'raspa' ya existe en el union EventType compartido.
      type: 'raspa',
      name: name.trim(),
      prizeKind: top?.prizeKind,
      prizeAmount: top && top.amount > 0 ? String(top.amount) : undefined,
      prizeDescription: 'Raspadita con premios ponderados',
      durationMin: dur,
      status,
      config: {
        prizes,
        cols: 3,
        rows: 3,
        matchToWin: 3,
        wagerMultiplier: 1,
        oncePerClient: true,
      },
    };
  };

  const submit = async (status: 'draft' | 'active') => {
    setError('');
    if (!name.trim()) {
      setError('El nombre de la campaña es requerido');
      return;
    }
    if (rows.length === 0) {
      setError('Agregá al menos un premio');
      return;
    }
    if (!totalOk) {
      setError(`La probabilidad total debe sumar 100% (actual: ${totalWeight}%)`);
      return;
    }
    try {
      await createMutation.mutateAsync(buildBody(status));
      // reset
      setName('');
      setDurationMin('15');
      setRows(DEFAULT_ROWS.map((r) => ({ ...r })));
      onCreated?.();
    } catch {
      /* el hook ya muestra toast de error */
    }
  };

  const saveTemplate = () => {
    try {
      const tpl = JSON.stringify(
        { name: name.trim() || 'Plantilla Raspa', durationMin: Number(durationMin) || 0, rows },
        null,
        2,
      );
      localStorage.setItem('raspa_template', tpl);
      setError('');
    } catch {
      /* noop */
    }
  };

  const pending = createMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Ticket className="h-5 w-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-amber-500">Crear Raspa y Gana</h3>
      </div>

      <div className="space-y-2">
        <Label htmlFor="raspa-name">
          Nombre de la campaña <span className="text-red-500">*</span>
        </Label>
        <Input
          id="raspa-name"
          placeholder="ej: Raspa Viernes Loco"
          value={name}
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
        />
      </div>

      <div className="space-y-2 md:max-w-[14rem]">
        <Label htmlFor="raspa-duration">
          Duración (minutos) <span className="text-red-500">*</span>
        </Label>
        <Input
          id="raspa-duration"
          type="number"
          min="1"
          value={durationMin}
          onChange={(e) => setDurationMin((e.target as HTMLInputElement).value)}
        />
        <p className="text-xs text-muted-foreground">
          Se cierra automáticamente después de este tiempo
        </p>
      </div>

      <div className="space-y-3">
        <Label>Premios (probabilidad total debe sumar 100%)</Label>

        {rows.map((row, idx) => (
          <div
            key={idx}
            className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 p-2"
          >
            {/* Emoji */}
            <Select
              value={row.emoji}
              onValueChange={(v) => updateRow(idx, { emoji: v })}
            >
              <SelectTrigger className="w-16 text-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMOJI_CHOICES.map((em) => (
                  <SelectItem key={em} value={em} className="text-lg">
                    {em}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Label */}
            <Input
              className="min-w-[8rem] flex-1"
              placeholder="Premio Mayor"
              value={row.label}
              onChange={(e) => updateRow(idx, { label: (e.target as HTMLInputElement).value })}
            />

            {/* Tipo */}
            <Select
              value={row.prizeKind}
              onValueChange={(v) => updateRow(idx, { prizeKind: v as PrizeKind })}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Monto $ */}
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">$</span>
              <Input
                className="w-24"
                type="number"
                min="0"
                disabled={row.prizeKind === 'none'}
                value={row.amount}
                onChange={(e) => updateRow(idx, { amount: (e.target as HTMLInputElement).value })}
              />
            </div>

            {/* Peso % */}
            <div className="flex items-center gap-1">
              <Input
                className="w-20"
                type="number"
                min="0"
                max="100"
                value={row.weight}
                onChange={(e) => updateRow(idx, { weight: (e.target as HTMLInputElement).value })}
              />
              <span className="text-muted-foreground">%</span>
            </div>

            {/* Eliminar */}
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="text-red-500"
              disabled={rows.length <= 1}
              onClick={() => removeRow(idx)}
              title="Quitar premio"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" size="sm" onClick={addRow}>
            <Plus className="mr-1 h-4 w-4" />
            Agregar premio
          </Button>
          <span
            className={`text-sm font-semibold ${totalOk ? 'text-green-500' : 'text-red-500'}`}
          >
            Total: {totalWeight}%
          </span>
        </div>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex flex-wrap gap-3 pt-2">
        <Button variant="outline" disabled={pending} onClick={() => submit('draft')}>
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar Borrador
        </Button>
        <Button
          className="bg-amber-500 text-amber-950 hover:bg-amber-400"
          disabled={pending}
          onClick={() => submit('active')}
        >
          {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear y Activar
        </Button>
        <Button variant="outline" type="button" onClick={saveTemplate}>
          <Box className="mr-2 h-4 w-4" />
          Guardar como Template
        </Button>
      </div>
    </div>
  );
}

export default RaspaForm;
